import React, { useEffect, useMemo, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { megaApi, ezugiApi } from "../lib/api";
import { getGames, peekGames, warmImages } from "../lib/megaCache";
import GlossCard from "./GlossCard";
import { useApp } from "../context/AppContext";
import { useToast } from "../hooks/use-toast";

// Shared launcher: creates the MegaNodes session and hands the browser straight to the provider's game URL.
export function useMegaLaunch() {
  const { isAuthed } = useApp();
  const { toast } = useToast();
  const [busy, setBusy] = useState(null);
  const launch = async (g) => {
    if (!isAuthed) { document.querySelector('[data-testid="nav-signin-btn"]')?.click(); return; }
    if (g.maintenance) { toast({ title: "Under maintenance", description: `${g.name} is temporarily unavailable.` }); return; }
    if (busy) return;
    setBusy(g.game_symbol);
    try {
      const { launch_url } = g.provider_code === "hub"
        ? await ezugiApi.launch(g.game_symbol)
        : await megaApi.launch(g.provider_code, g.game_symbol);
      window.location.href = launch_url;
      setTimeout(() => setBusy(null), 15000); // navigation normally unmounts us first
    } catch (e) {
      setBusy(null);
      toast({ title: "Could not open game", description: e?.response?.data?.detail || (e?.code === "ECONNABORTED" ? "The casino provider is slow right now. Please try again." : "Please try again.") });
    }
  };
  return { launch, busy };
}

// Home-tab variant: same glossy GlossCard as the rest of the Home grid (4:3, ribbon CTA).
function HomeCard({ g, onLaunch, launching }) {
  const [img, setImg] = useState(g.image ? "loading" : "none");
  return (
    <GlossCard light onClick={() => onLaunch(g)} disabled={launching} name={g.name} testid={`mega-game-${g.game_symbol}`} nameTestid={`mega-name-${g.game_symbol}`}>
      {img === "loading" && <div className="bbw-skeleton absolute inset-0" />}
      {g.image && img !== "error" && (
        <img src={g.image} alt={g.name} loading="eager" decoding="async" fetchPriority="high" onLoad={() => setImg("ok")} onError={() => setImg("error")}
          className={`bbw-img absolute inset-0 h-full w-full object-cover ${img === "ok" ? "opacity-100" : "opacity-0"}`} />
      )}
      {g.maintenance && <span className="absolute top-2 left-2 rounded-sm bg-[#e53935] px-1.5 py-0.5 text-[10px] font-bold text-white">Maintenance</span>}
      {launching && <span className="absolute inset-0 z-10 grid place-items-center bg-black/50"><Loader2 className="h-7 w-7 text-[#f5b400] animate-spin" /></span>}
    </GlossCard>
  );
}

export function MegaCard({ g, onLaunch, busy, variant = "lobby", eager = false }) {
  const [img, setImg] = useState(g.image ? "loading" : "none");
  const launching = busy === g.game_symbol;
  if (variant === "home") return <HomeCard g={g} onLaunch={onLaunch} launching={launching} />;
  return (
    <button type="button" onClick={() => onLaunch(g)} disabled={launching} data-testid={`mega-game-${g.game_symbol}`}
      aria-label={g.name}
      className="mega-tile bbw-card block w-full text-left">
      <div className="bbw-card-in aspect-[1.45/1] w-full">
        {img === "loading" && <div className="bbw-skeleton absolute inset-0" />}
        {g.image && img !== "error" && (
          <img src={g.image} alt="" loading={eager ? "eager" : "lazy"} decoding="async" fetchPriority={eager ? "high" : "auto"}
            onLoad={() => setImg("ok")} onError={() => setImg("error")}
            className={`bbw-img absolute inset-0 h-full w-full object-cover [image-rendering:auto] ${img === "ok" ? "opacity-100" : "opacity-0"}`} />
        )}
        <div className="bbw-name-fade absolute inset-x-0 bottom-0 h-[52%] pointer-events-none" />
        <span className="absolute left-2.5 right-2.5 bottom-2 truncate text-[15px] sm:text-base font-semibold text-white leading-tight" data-testid={`mega-name-${g.game_symbol}`}>
          {g.name}
        </span>
        <span className="bbw-shine" />
        {g.tag && (
          <span className="absolute top-2 left-2 rounded-md bg-[#E0B93A] px-1.5 py-0.5 text-[9px] font-extrabold uppercase tracking-wide text-black" data-testid={`mega-tag-${g.game_symbol}`}>{g.tag}</span>
        )}
        {g.maintenance && <span className="absolute top-2 right-2 rounded-md bg-[#e53935] px-1.5 py-0.5 text-[9px] font-bold uppercase text-white">Maintenance</span>}
        {launching && (
          <span className="absolute inset-0 grid place-items-center bg-black/50"><Loader2 className="h-7 w-7 text-[#f5b400] animate-spin" /></span>
        )}
      </div>
    </button>
  );
}

export default function MegaLobby({ category = "Live", query = "", providerFilter = null, match = null, emptyText = "Live tables are being set up.", className = "", variant = "lobby", extra = null, hubFirst = 0 }) {
  const cached = peekGames(category);
  const [data, setData] = useState(cached ? { ...cached, loading: false } : { games: [], providers: [], configured: true, loading: true });
  const { launch, busy } = useMegaLaunch();
  useEffect(() => {
    let dead = false;
    getGames(category).then((d) => { if (!dead) { setData({ ...d, loading: false }); warmImages(d.games, variant === "home" ? 12 : 30); } })
      .catch(() => { if (!dead) setData((p) => ({ ...p, loading: false })); });
    return () => { dead = true; };
  }, [category, variant]);
  const PAGE = 24;
  const [limit, setLimit] = useState(PAGE);
  const sentinelRef = useRef(null);
  useEffect(() => { setLimit(PAGE); }, [category, query, providerFilter, match]);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return undefined;
    const io = new IntersectionObserver((entries) => { if (entries.some((e) => e.isIntersecting)) setLimit((n) => n + PAGE); }, { rootMargin: "600px 0px" });
    io.observe(el);
    return () => io.disconnect();
  });
  const list = useMemo(() => {
    const q = (query || "").trim().toLowerCase();
    const keep = (g) => (!providerFilter || g.provider_code === providerFilter) && (!match || match(g))
      && (!q || (g.name || "").toLowerCase().includes(q) || (g.provider_name || "").toLowerCase().includes(q));
    const mega = data.games.filter(keep);
    const hub = (extra || []).filter(keep);  // hub (Ezugi) tables blended in: one Ezugi, one MegaNodes, alternating
    const out = hub.slice(0, hubFirst || 0);  // optional block of Ezugi tables pinned at the top (e.g. Roulette)
    const rest = hub.slice(out.length);
    for (let i = 0; i < Math.max(mega.length, rest.length); i++) {
      if (rest[i]) out.push(rest[i]);
      if (mega[i]) out.push(mega[i]);
    }
    return out;
  }, [data.games, extra, query, providerFilter, match, hubFirst]);
  if (data.loading) return <div className="py-12 grid place-items-center text-slate-400" data-testid="mega-lobby-loading"><Loader2 className="h-6 w-6 animate-spin text-[#f5b400]" /></div>;
  if (!list.length) return <div className="py-10 text-center text-sm text-slate-400" data-testid="mega-lobby-empty">{data.configured ? "No games found." : emptyText}</div>;
  const shown = variant === "home" ? list : list.slice(0, limit);
  return (
    <>
      <div className={`grid grid-cols-2 ${variant === "home" ? "gap-1.5 pt-1.5" : "gap-3 p-3"} ${className}`} data-testid={`mega-lobby-${category.toLowerCase()}`}>
        {shown.map((g, i) => <MegaCard key={`${g.provider_code}-${g.game_symbol}`} g={g} onLaunch={launch} busy={busy} variant={variant} eager={i < Math.max(12, hubFirst || 0)} />)}
      </div>
      {shown.length < list.length && <div ref={sentinelRef} className="h-10" data-testid={`mega-lobby-${category.toLowerCase()}-more`} />}
    </>
  );
}
